import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { recordAuthEvent } from "@/contexts/AuthContext";
import { lovable } from "@/integrations/lovable";
import { supabase } from "@/integrations/supabase/client";
import { isNativeApp } from "@/lib/platform";
import { tapMedium } from "@/lib/haptics";
import { errorMessage } from "@/lib/error-message";

/**
 * Shared Google sign-in handler used by both the desktop/web Auth page
 * and the native Android Welcome screen. Encapsulates the
 * native-vs-web branching so screens only worry about UI.
 */
export function useGoogleSignIn() {
  const { toast } = useToast();
  const [signingIn, setSigningIn] = useState(false);

  const signInWithGoogle = async () => {
    void tapMedium();
    setSigningIn(true);
    recordAuthEvent("oauth-initiated", isNativeApp() ? "native" : "web");

    if (isNativeApp()) {
      try {
        const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
        try { await GoogleAuth.initialize(); } catch { /* already initialized */ }
        const user = await GoogleAuth.signIn();
        const idToken = user?.authentication?.idToken;
        if (!idToken) throw new Error("Google did not return an ID token");
        const { error } = await supabase.auth.signInWithIdToken({
          provider: "google",
          token: idToken,
        });
        if (error) throw error;
        recordAuthEvent("set-session-success", "native-id-token");
        return;
      } catch (e) {
        setSigningIn(false);
        const msg = errorMessage(e);
        if (/cancel|closed|popup_closed|12501/i.test(msg)) return;
        recordAuthEvent("set-session-failure", msg.slice(0, 80));
        toast({
          title: "Google sign-in failed",
          description: msg,
          variant: "destructive",
        });
        return;
      }
    }

    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/app`,
    });
    if (result.redirected) return;
    if (result.error) {
      setSigningIn(false);
      toast({
        title: "Google sign-in failed",
        description: result.error.message,
        variant: "destructive",
      });
    }
  };

  return { signingIn, signInWithGoogle };
}
